/* eslint-disable consistent-return */
import { useContext, useEffect, useRef, useReducer } from "react";
import FetchError from "../../util/FetchError";
import accessResult from "./accessResult";
import defaultOptions from "./defaultOptions";
import FetchContext from "./fetchContext";
import invalidate, {
  invalidateEndpoints,
  invalidateEndpoint,
  setCacheValue,
} from "./cacheActions";
import useDataWithOptimisticUpdates from "../useDataWithOptimisticUpdates.jsx";
import useEventCallback from "../useEventCallback";
import emitter from "./emitter";
import doUpsert from "./upsert";
import domains from "../../services/domains";
import ApiError from "../../util/ApiError";
import { useLoadContext } from "../../components/LoadBoundary";

const defaultUpdateOpts = {
  refetch: true,
};

/**
 * Don't pass an inline function for `endpoint`.
 * It's used as the key in the cache and needs to be the same between renders.
 */
function useFetch(endpoint, input, optionOverrides) {
  const ctx = useContext(FetchContext);
  const loadCtx = useLoadContext();
  const mounted = useRef(false);
  const renderedVersion = useRef();
  const [, forceRender] = useReducer((s) => s + 1, 0);

  const options = {
    ...defaultOptions,
    ...ctx.options,
    ...optionOverrides,
  };

  const entry = accessResult(ctx, endpoint, input, options);
  const { value, error, promise, key, resolvedAt, version } = entry;

  renderedVersion.current = version;

  const [optimisticData, applyUpdate] = useDataWithOptimisticUpdates(
    value,
    key,
  );

  async function refetch() {
    invalidate(ctx, endpoint, input, options);
    const { promise: prom } = accessResult(ctx, endpoint, input, options);
    return prom;
  }

  const upsert = useEventCallback((fn) => {
    if (!mounted.current) {
      throw new Error(
        "useFetch(): Can't upsert new data during render or directly on mount",
      );
    }
    if (value === undefined) {
      throw new Error(
        "useFetch(): Can't upsert new data before data has finished loading",
      );
    }
    doUpsert(ctx, endpoint, input, options, fn);
  });

  const applyOptimisticUpdate = useEventCallback(async (fn, prom, userOpts) => {
    const opts = {
      ...defaultUpdateOpts,
      ...userOpts,
    };

    if (!mounted.current) {
      throw new Error(
        "useFetch(): Can't apply update during render or directly on mount",
      );
    }
    if (value === undefined) {
      throw new Error(
        "useFetch(): Can't apply update before data has finished loading",
      );
    }
    const res = await applyUpdate(
      fn,
      prom.then(async (r) => {
        if (opts.refetch) await refetch();
        return r;
      }),
    );
    return res;
  });

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      options.refetchOnMount &&
      !promise &&
      resolvedAt &&
      Date.now() - resolvedAt > 5000
    ) {
      invalidate(ctx, endpoint, input, options);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    if (!promise || optimisticData === undefined) return;

    loadCtx.add(promise);

    return () => {
      loadCtx.remove(promise);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promise]);

  // force render if fetch promise resolved between render and effects
  useEffect(() => {
    if (entry.version !== renderedVersion.current) {
      forceRender();
    }
  });

  useEffect(() => {
    let didUnsubscribe = false;

    function checkForUpdates(e) {
      if (didUnsubscribe) {
        return;
      }
      if (!e.endpoint || e.endpoint === endpoint) forceRender();
    }

    emitter.on("update", checkForUpdates);

    return () => {
      didUnsubscribe = true;
      emitter.off("update", checkForUpdates);
    };
  }, [endpoint]);

  return {
    get data() {
      if (optimisticData === undefined) {
        if (promise && options.suspense) throw promise;
        if (error) {
          if (options.throwOnError) {
            throw error instanceof ApiError
              ? error
              : new FetchError(error.message, refetch, error.info);
          }
        }
      }
      return optimisticData;
    },
    loading: Boolean(promise),
    error,
    refetch,
    key,
    applyOptimisticUpdate,
    upsert,
  };
}

function useCacheActions() {
  const ctx = useContext(FetchContext);

  const waitForEntriesToSettle = async (
    entries,
    { timeoutMs = 20000, pollMs = 100 } = {},
  ) => {
    if (!entries.length) return true;

    const startedAt = Date.now();
    let sawRefetch = entries.some((entry) => Boolean(entry?.promise));

    while (Date.now() - startedAt <= timeoutMs) {
      const hasPendingRequest = entries.some((entry) =>
        Boolean(entry?.promise),
      );
      const hasRequestError = entries.some((entry) => Boolean(entry?.error));

      if (hasPendingRequest) {
        sawRefetch = true;
      }

      if (hasRequestError) {
        return false;
      }

      if (!hasPendingRequest && sawRefetch) {
        return true;
      }

      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => {
        setTimeout(resolve, pollMs);
      });
    }

    return false;
  };

  const invalidateEndpointsAndWait = async (
    endpoints,
    { timeoutMs = 20000, pollMs = 100 } = {},
  ) => {
    const targetEntries = endpoints.flatMap((endpoint) => {
      const entriesForResource = ctx.cache.entries.get(endpoint);
      return entriesForResource ? Array.from(entriesForResource.values()) : [];
    });

    invalidateEndpoints(ctx, endpoints);

    return waitForEntriesToSettle(targetEntries, { timeoutMs, pollMs });
  };

  return {
    purge: ctx.cache.purge,
    invalidateEndpoints: invalidateEndpoints.bind(null, ctx),
    invalidateEndpoint: invalidateEndpoint.bind(null, ctx),
    invalidateDomain: (d) => invalidateEndpoints(ctx, domains[d]),
    invalidateDomainAndWait: (d, options) =>
      invalidateEndpointsAndWait(domains[d] || [], options),
    setCacheValue: (endpoint, input, value, options) => {
      setCacheValue(ctx, endpoint, input, value, {
        ...defaultOptions,
        ...ctx.options,
        ...options,
      });
    },
  };
}

export default useFetch;
export { useCacheActions };
